/**
 * apiClient — instance Axios partagée pour toute communication backend.
 *
 * @remarks
 * - **Base URL** : relatif `''` en dev (proxy Vite forward `/api/*` → gateway,
 *   cookies first-party donc bypass third-party blocking) ; `VITE_API_URL`
 *   en prod (nginx reverse proxy).
 * - **Token** : injecté en `Authorization: Bearer <token>` depuis le store
 *   en mémoire (jamais localStorage).
 * - **withCredentials: true** : nécessaire pour envoyer le cookie HTTP-only
 *   `refresh_token`.
 *
 * **Response interceptor 401** : tente d'abord un **silent refresh** (via
 * `lib/auth/refresh.ts`, déduplique les requêtes simultanées). Si le refresh
 * réussit, **rejoue la requête originale** avec le nouveau token. Sinon,
 * `logout()` automatique. La requête originale est marquée `_retried` pour
 * éviter une boucle infinie sur le 401 du `/refresh` lui-même.
 *
 * @example
 * ```ts
 * import { apiClient } from '@/lib/http/client';
 * const { data } = await apiClient.post<ApiResponse<SignInResponse>>(
 *   '/api/auth/signin',
 *   { email, password },
 * );
 * ```
 */

import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/app/stores/use-auth-store';
import { attemptSilentRefresh } from '@/lib/auth/refresh';

const BASE_URL = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 10_000,
  withCredentials: true,
});

// ── Request interceptor : injecte le Bearer token ───────────────────────────
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Dev only : latence artificielle pour visualiser les loaders ─────────────
// `VITE_API_DELAY_MS=3000` dans `.env` → chaque requête attend 3 s avant de
// partir. Ignoré hors `import.meta.env.DEV` (jamais actif en prod) et en mode
// `test` (Vitest charge aussi `.env` — sans cette garde les tests du client
// dépasseraient leur timeout).
const API_DELAY_MS =
  import.meta.env.DEV && import.meta.env.MODE !== 'test'
    ? Number(import.meta.env.VITE_API_DELAY_MS ?? 0)
    : 0;

if (API_DELAY_MS > 0) {
  apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) =>
      new Promise((resolve) => {
        setTimeout(() => {
          resolve(config);
        }, API_DELAY_MS);
      }),
  );
}

// ── Response interceptor : 401 → silent refresh + rejouer la requête ────────
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryableConfig | undefined;
    const status = error.response?.status;

    // Pas un 401, requête sans config (improbable), ou déjà retry → propager.
    if (status !== 401 || !original || original._retried === true) {
      return Promise.reject(error);
    }

    // Évite la boucle : on ne refresh PAS pour le `/refresh` lui-même.
    // `endsWith` (pas `includes`) pour ne pas matcher accidentellement un futur
    // endpoint comme `/api/auth/refresh-metadata`.
    if (original.url?.endsWith('/api/auth/refresh')) {
      useAuthStore.getState().logout({ expired: true });
      return Promise.reject(error);
    }

    original._retried = true;
    const refreshed = await attemptSilentRefresh();
    if (!refreshed) {
      // logout() est déjà appelé par attemptSilentRefresh en cas d'échec.
      return Promise.reject(error);
    }

    // Rejouer la requête originale (le nouveau token sera injecté par le
    // request interceptor au-dessus).
    return apiClient.request(original);
  },
);
